import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Box,
  Typography,
  Grid,
  Paper,
  Switch,
  FormControlLabel,
  Collapse
} from '@mui/material';
import { Plus, Trash2, ChevronDown, ChevronRight, X } from 'lucide-react';

const PropertyEntry = ({ property, onChange, onDelete, level = 0 }) => {
  const [expanded, setExpanded] = useState(false);

  const handleNestedPropertyAdd = () => {
    onChange({
      ...property,
      properties: [...(property.properties || []), {
        name: '',
        type: 'string',
        description: '',
        required: false,
        isArray: false,
        properties: []
      }]
    });
  };

  const handleNestedPropertyChange = (index, newValue) => {
    const updatedProperties = [...(property.properties || [])];
    updatedProperties[index] = newValue;
    onChange({
      ...property,
      properties: updatedProperties
    });
  };

  const handleNestedPropertyDelete = (index) => {
    onChange({
      ...property,
      properties: property.properties.filter((_, i) => i !== index)
    });
  };

  return (
    <Box sx={{ ml: level * 3, mb: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            label="Name"
            value={property.name}
            onChange={(e) => onChange({ ...property, name: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Type</InputLabel>
            <Select
              value={property.type}
              label="Type"
              onChange={(e) => onChange({ ...property, type: e.target.value })}
            >
              <MenuItem value="string">String</MenuItem>
              <MenuItem value="number">Number</MenuItem>
              <MenuItem value="boolean">Boolean</MenuItem>
              <MenuItem value="object">Object</MenuItem>
              <MenuItem value="array">Array</MenuItem>
              <MenuItem value="date">Date</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            label="Description"
            value={property.description}
            onChange={(e) => onChange({ ...property, description: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={property.required}
                  onChange={(e) => onChange({ ...property, required: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#edbc45',
                      '&:hover': {
                        backgroundColor: 'rgba(237, 188, 69, 0.08)',
                      },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#edbc45',
                    },
                  }}
                />
              }
              label="Required"
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={property.isArray}
                  onChange={(e) => onChange({ ...property, isArray: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#edbc45',
                      '&:hover': {
                        backgroundColor: 'rgba(237, 188, 69, 0.08)',
                      },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#edbc45',
                    },
                  }}
                />
              }
              label="Array"
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={1}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {(property.type === 'object' || property.type === 'array') && (
              <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              </IconButton>
            )}
            {onDelete && (
              <IconButton size="small" onClick={onDelete} color="error">
                <Trash2 size={20} />
              </IconButton>
            )}
          </Box>
        </Grid>
      </Grid>

      {(property.type === 'object' || property.type === 'array') && (
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, ml: 3 }}>
            {property.properties?.map((prop, index) => (
              <PropertyEntry
                key={index}
                property={prop}
                onChange={(newValue) => handleNestedPropertyChange(index, newValue)}
                onDelete={() => handleNestedPropertyDelete(index)}
                level={level + 1}
              />
            ))}
            <Button
              size="small"
              startIcon={<Plus size={16} />}
              onClick={handleNestedPropertyAdd}
              variant="contained"
              sx={{
                mt: 1,
                backgroundColor: '#edbc45',
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#d4a63a'
                }
              }}
            >
              Add Property
            </Button>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const ManualObjectEntry = ({ onAddObject }) => {
  const [open, setOpen] = useState(false);
  const [object, setObject] = useState({
    name: '',
    type: 'object',
    description: '',
    isRequest: false,
    isResponse: false,
    properties: []
  });

  const handleAddProperty = () => {
    setObject(prev => ({
      ...prev,
      properties: [...prev.properties, {
        name: '',
        type: 'string',
        description: '',
        required: false,
        isArray: false,
        properties: []
      }]
    }));
  };

  const handlePropertyChange = (index, newValue) => {
    const updatedProperties = [...object.properties];
    updatedProperties[index] = newValue;
    setObject(prev => ({
      ...prev,
      properties: updatedProperties
    }));
  };

  const handlePropertyDelete = (index) => {
    setObject(prev => ({
      ...prev,
      properties: prev.properties.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = () => {
    onAddObject(object);
    setOpen(false);
    setObject({
      name: '',
      type: 'object',
      description: '',
      isRequest: false,
      isResponse: false,
      properties: []
    });
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<Plus />}
        onClick={() => setOpen(true)}
        sx={{ mb: 2 }}
      >
        Add Object
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#f8f4ff',
          }
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            bgcolor: '#edbc45',
            color: '#000',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 600
          }}
        >
          Add New Object
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              color: '#000',
              p: 0.5,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Object Name"
                value={object.name}
                onChange={(e) => setObject(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Description"
                value={object.description}
                onChange={(e) => setObject(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={object.isRequest}
                    onChange={(e) => setObject(prev => ({ ...prev, isRequest: e.target.checked }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#edbc45',
                        '&:hover': {
                          backgroundColor: 'rgba(237, 188, 69, 0.08)',
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#edbc45',
                      },
                    }}
                  />
                }
                label="Is Request Object"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={object.isResponse}
                    onChange={(e) => setObject(prev => ({ ...prev, isResponse: e.target.checked }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#edbc45',
                        '&:hover': {
                          backgroundColor: 'rgba(237, 188, 69, 0.08)',
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#edbc45',
                      },
                    }}
                  />
                }
                label="Is Response Object"
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 2 }}>Properties</Typography>
          
          {object.properties.map((prop, index) => (
            <PropertyEntry
              key={index}
              property={prop}
              onChange={(newValue) => handlePropertyChange(index, newValue)}
              onDelete={() => handlePropertyDelete(index)}
            />
          ))}

          <Button
            variant="contained"
            startIcon={<Plus />}
            onClick={handleAddProperty}
            sx={{
              mt: 2,
              backgroundColor: '#edbc45',
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#d4a63a'
              }
            }}
          >
            Add Property
          </Button>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            sx={{ color: '#000', borderColor: '#000' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              backgroundColor: '#edbc45',
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#d4a63a'
              }
            }}
          >
            Add Object
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManualObjectEntry;